/* Native interactions, scoped to their section and safe to reload in the editor. */
(() => {
  if (window.kehwahSystem) return;
  document.documentElement.classList.add('ks-js');
  const motion = () => matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  const track = (name, data = {}) => window.kehwahTrack?.(name, data);
  let activeDialog;
  function openDialog(dialog, trigger) {
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    if (activeDialog && activeDialog !== dialog) closeDialog(activeDialog);
    activeDialog = dialog;
    dialog._ksTrigger = trigger;
    trigger?.setAttribute('aria-expanded', 'true');
    document.documentElement.dataset.ksOverlay = 'true';
    if (!dialog.open) dialog.showModal();
    return true;
  }
  function releaseDialog(dialog) {
    if (activeDialog === dialog) { activeDialog = null; delete document.documentElement.dataset.ksOverlay; }
    dialog._ksTrigger?.setAttribute('aria-expanded', 'false');
    dialog._ksTrigger?.focus({ preventScroll: true });
    dialog._ksTrigger = null;
    dialog.querySelector('ks-search')?.cancel();
    document.dispatchEvent(new CustomEvent('kehwah:overlay-close'));
  }
  function closeDialog(dialog) { if (!dialog) return; if (dialog.open) dialog.close(); releaseDialog(dialog); }
  function bindDialog(dialog, signal) {
    dialog.addEventListener('keydown', e => {
      if (e.key !== 'Tab') return;
      const stops = [...dialog.querySelectorAll('a[href],button,input,select,textarea,summary,[tabindex]')]
        .filter(el => !el.disabled && el.tabIndex >= 0 && el.getClientRects().length);
      const first = stops[0], last = stops[stops.length - 1];
      if (!first) { e.preventDefault(); dialog.focus(); return; }
      if (e.shiftKey && (document.activeElement === first || document.activeElement === dialog)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }, { signal });
    dialog.addEventListener('click', e => {
      if (e.target.closest('[data-ks-close]')) dialog.close();
      else if (e.target === dialog) {
        const r = dialog.getBoundingClientRect();
        if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) dialog.close();
      }
    }, { signal });
    dialog.addEventListener('close', () => releaseDialog(dialog), { signal });
  }
  class SectionElement extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.mount();
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.cleanup?.();
    }
    on(target, name, fn, options = {}) { target?.addEventListener(name, fn, { ...options, signal: this.signal }); }
    mount() {}
  }
  class Header extends SectionElement {
    mount() {
      this.querySelectorAll('dialog').forEach(d => bindDialog(d, this.signal));
      this.on(this, 'click', e => {
        const trigger = e.target.closest('[data-ks-open]');
        if (trigger && openDialog(document.getElementById(trigger.dataset.ksOpen), trigger)) {
          e.preventDefault();
          track('navigation_open', { navigation_name: trigger.dataset.ksOpen.startsWith('Menu-') ? 'mobile' : 'search' });
        }
      });
      const menus = [...this.querySelectorAll('.ks-nav > details')];
      menus.forEach(d => this.on(d, 'toggle', () => {
        if (d.open) menus.forEach(other => { if (other !== d) other.open = false; });
      }));
      this.on(document, 'click', e => { if (!this.contains(e.target)) menus.forEach(d => d.open = false); });
      this.on(this, 'keydown', e => {
        if (e.key === 'Escape') menus.filter(d => d.open).forEach(d => { d.open = false; d.querySelector('summary')?.focus(); });
      });
      this.resize = new ResizeObserver(() => {
        document.documentElement.style.setProperty('--ks-header', `${Math.ceil(this.getBoundingClientRect().height)}px`);
        if (innerWidth >= 1021) this.querySelector('dialog[id^="Menu-"][open]')?.close();
      });
      this.resize.observe(this);
    }
    cleanup() { this.resize?.disconnect(); this.querySelectorAll('dialog[open]').forEach(d => closeDialog(d)); }
  }
  class Search extends SectionElement {
    mount() {
      this.input = this.querySelector('input[type=search]');
      this.results = this.querySelector('[data-ks-search-results]');
      this.status = this.querySelector('[data-ks-search-status]');
      this.sequence = 0;
      this.on(this.input, 'input', () => {
        this.cancel();
        const query = this.input.value.trim();
        if (query.length < 2) { this.results.replaceChildren(); this.status.textContent = ''; return; }
        this.timer = setTimeout(() => this.search(query), 250);
      });
      this.on(this.querySelector('form'), 'submit', () => track('search_submit'));
      this.on(this.results, 'click', e => { if (e.target.closest('a')) track('search_result_select'); });
    }
    cancel() { clearTimeout(this.timer); this.request?.abort(); this.sequence++; }
    async search(query) {
      const sequence = this.sequence;
      this.request = new AbortController();
      this.status.textContent = 'Finding your cup…';
      try {
        const url = new URL((window.Shopify?.routes?.root || '/') + 'search/suggest', location.origin);
        url.search = new URLSearchParams({ q: query, section_id: 'ks-predictive-search', 'resources[type]': 'product,page,article', 'resources[limit]': '5', 'resources[options][unavailable_products]': 'last' }).toString();
        const response = await fetch(url, { signal: this.request.signal, credentials: 'same-origin' });
        if (!response.ok) throw Error('search');
        const html = await response.text();
        if (sequence !== this.sequence) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const content = doc.querySelector('[data-ks-predictive-body]');
        if (!content) throw Error('search');
        this.results.replaceChildren(...content.childNodes);
        const count = Number(this.results.querySelector('[data-result-count]')?.dataset.resultCount || 0);
        this.status.textContent = count ? `${count} suggestions. Use the links below or search for all results.` : 'No suggestions. Try another tea or ingredient, or view all results.';
        if (!count) track('search_no_results');
      } catch (error) {
        if (error.name !== 'AbortError' && sequence === this.sequence) {
          this.results.replaceChildren(); this.status.textContent = 'Press Search to see all results.';
        }
      }
    }
    cleanup() { this.cancel(); }
  }
  class Gallery extends SectionElement {
    mount() {
      this.track = this.querySelector('[data-ks-gallery-track]');
      this.slides = [...this.querySelectorAll('.ks-gallery__slide')];
      this.thumbs = [...this.querySelectorAll('[data-ks-thumb]')];
      this.index = 0;
      this.dialog = this.querySelector('dialog');
      bindDialog(this.dialog, this.signal);
      this.on(this, 'click', e => {
        const thumb = e.target.closest('[data-ks-thumb]');
        if (thumb) this.go(Number(thumb.dataset.ksThumb));
        if (e.target.closest('[data-ks-gallery-prev]')) this.go(this.index - 1);
        if (e.target.closest('[data-ks-gallery-next]')) this.go(this.index + 1);
        const zoom = e.target.closest('[data-ks-zoom]');
        if (zoom) {
          const image = zoom.closest('figure').querySelector('img');
          const target = this.dialog.querySelector('[data-ks-zoom-image]');
          target.src = image.currentSrc || image.src; target.alt = image.alt;
          openDialog(this.dialog, zoom);
        }
      });
      this.on(this.track, 'scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update(Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth))));
      }, { passive: true });
      this.on(this.track, 'keydown', e => {
        if (e.target !== this.track) return;
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index + (e.key === 'ArrowRight' ? 1 : -1)); }
      });
      this.update(0);
    }
    go(index) {
      index = Math.max(0, Math.min(this.slides.length - 1, index));
      this.track.scrollTo({ left: index * this.track.clientWidth, behavior: motion() });
    }
    update(index) {
      this.index = Math.max(0, Math.min(this.slides.length - 1, index));
      this.thumbs.forEach((t,i) => t.setAttribute('aria-current', String(i === this.index)));
      const prev = this.querySelector('[data-ks-gallery-prev]'), next = this.querySelector('[data-ks-gallery-next]');
      if (prev) prev.disabled = this.index === 0;
      if (next) next.disabled = this.index >= this.slides.length - 1;
      const status = this.querySelector('[data-ks-gallery-count]');
      if (status) status.textContent = status.dataset.compact ? `${this.index + 1} / ${this.slides.length}` : `Image ${this.index + 1} of ${this.slides.length}`;
    }
    cleanup() { cancelAnimationFrame(this.frame); if (this.dialog?.open) closeDialog(this.dialog); }
  }
  class Product extends SectionElement {
    mount() {
      this.data = JSON.parse(this.querySelector('[data-ks-variants]').textContent);
      this.form = this.querySelector('[data-ks-product-form]');
      this.primary = this.querySelector('[data-ks-primary]');
      this.quantity = this.form.querySelector('[name=quantity]');
      this.sticky = this.querySelector('[data-ks-sticky]');
      this.variant = this.data.variants.find(v => String(v.id) === String(this.data.selected));
      this.on(this.form, 'change', e => {
        if (e.target.name === 'id') this.select(e.target.value);
        this.sync();
      });
      this.on(this.form, 'input', () => this.sync());
      this.on(this, 'click', e => {
        const quantity = e.target.closest('[data-ks-quantity]');
        if (quantity && this.form.getAttribute('aria-busy') !== 'true') {
          const value = Number(this.quantity.value);
          this.quantity.value = String(Math.max(1, (Number.isSafeInteger(value) ? value : 1) + Number(quantity.dataset.ksQuantity)));
          this.quantity.dispatchEvent(new Event('change', { bubbles: true }));
        }
        const chapter = e.target.closest('.ks-product-links a');
        if (chapter) this.querySelectorAll('.ks-product-links a').forEach(link => link.setAttribute('aria-current', String(link === chapter)));
        if (e.target.closest('[data-ks-sticky-add]') && !this.primary.disabled) this.form.requestSubmit(this.primary);
      });
      const header = document.querySelector('.ks-header-section');
      const headerInset = header && getComputedStyle(header).position === 'sticky' ? Math.ceil(header.getBoundingClientRect().height) : 0;
      this.observer = new IntersectionObserver(entries => {
        this.above = !entries[0].isIntersecting && entries[0].boundingClientRect.bottom <= (entries[0].rootBounds?.top || 0) + 1;
        this.sync();
      }, { rootMargin: `-${headerInset}px 0px 0px 0px`, threshold: 0 });
      this.observer.observe(this.form.querySelector('.ks-purchase-actions'));
      this.mutations = new MutationObserver(() => this.sync());
      this.mutations.observe(this.primary, { attributes: true, attributeFilter: ['disabled'] });
      this.on(document, 'focusin', () => this.sync());
      this.on(document, 'focusout', () => requestAnimationFrame(() => this.sync()));
      this.on(document, 'kehwah:overlay-close', () => this.sync());
      this.on(document, 'kehwah:cart-state', () => this.sync());
      // Shopify's preview toolbar is a top-layer popover. Its public wrapper
      // exposes its real height even though the inner iframe fills the viewport.
      this.previewResize = new ResizeObserver(() => this.updatePreviewInset());
      this.previewWatch = new MutationObserver(() => {
        const wrapper = document.getElementById('PBarNextFrameWrapper');
        if (wrapper && wrapper !== this.previewWrapper) {
          this.previewWrapper = wrapper; this.previewResize.observe(wrapper);
        }
        this.updatePreviewInset();
      });
      this.previewWatch.observe(document.body, { childList: true });
      this.previewWrapper = document.getElementById('PBarNextFrameWrapper');
      if (this.previewWrapper) this.previewResize.observe(this.previewWrapper);
      this.updatePreviewInset();
      this.select(this.data.selected, false);
    }
    updatePreviewInset() {
      const rect = this.previewWrapper?.getBoundingClientRect();
      const height = rect && rect.bottom > 0 ? Math.ceil(rect.height) : 0;
      this.previewExpanded = height > 160;
      document.documentElement.style.setProperty('--ks-preview-inset', height <= 160 ? height + 'px' : '0px');
      this.sync();
    }
    select(id, notify = true) {
      const variant = this.data.variants.find(v => String(v.id) === String(id));
      if (!variant) return;
      this.variant = variant;
      const price = document.createElement('span'); price.className = 'ks-price';
      const current = document.createElement('strong'); current.textContent = variant.money; price.append(current);
      if (variant.compare) { const mrp = document.createElement('s'); mrp.textContent = variant.compare; mrp.setAttribute('aria-label', 'MRP ' + variant.compare); price.append(mrp); }
      if (variant.compare && variant.saving > 0) { const saving = document.createElement('span'); saving.className = 'ks-saving'; saving.textContent = variant.saving + '% off'; price.append(saving); }
      this.querySelector('[data-ks-main-price]').replaceChildren(price);
      this.form.querySelectorAll('button[type=submit]').forEach(button => {
        button.dataset.title = `${this.data.title} — ${variant.title}`;
        button.dataset.price = String(variant.price / 100);
      });
      window.kehwahProductCart?.setAvailability?.(this.form, variant.available);
      const image = this.querySelector('.ks-gallery__slide img');
      const url = variant.title === '50 Tea Bags' && this.data.refillImage ? this.data.refillImage : this.data.image;
      if (image && url) {
        image.src = url; image.removeAttribute('srcset'); image.alt = `${this.data.title} — ${variant.title}`;
        const thumb = this.querySelector('[data-ks-thumb="0"] img');
        if (thumb) { thumb.src = url; thumb.removeAttribute('srcset'); }
        this.querySelector('ks-gallery')?.go(0);
      }
      const note = this.querySelector('[data-ks-refill-note]');
      if (note) note.hidden = variant.title !== '50 Tea Bags';
      if (notify) {
        const url = new URL(location.href); url.searchParams.set('variant', String(variant.id));
        history.replaceState(history.state, '', url);
        track('variant_select', { item_id: this.dataset.productHandle });
      }
      this.sync();
    }
    sync() {
      if (!this.variant) return;
      const busy = this.form.getAttribute('aria-busy') === 'true';
      this.form.querySelectorAll('input, select').forEach(input => {
        if (input.name === 'id') input.disabled = busy || !this.data.variants.find(v => String(v.id) === input.value)?.available;
        else input.disabled = busy;
      });
      this.form.querySelectorAll('[data-ks-quantity]').forEach(b => b.disabled = busy);
      if (!this.sticky) return;
      const q = Number(this.quantity.value);
      const valid = Number.isSafeInteger(q) && q >= 1;
      const button = this.sticky.querySelector('[data-ks-sticky-add]');
      button.disabled = this.primary.disabled || !valid;
      button.textContent = this.primary.textContent;
      this.sticky.querySelector('[data-ks-sticky-label]').textContent = `${this.variant.title} · ${valid ? q : 1} × ${this.variant.money}`;
      const typing = innerWidth < 768 && document.activeElement?.matches('input, textarea, select, [contenteditable=true]');
      this.sticky.hidden = !this.above || typing || this.previewExpanded || Boolean(document.documentElement.dataset.ksOverlay);
    }
    cleanup() { this.observer?.disconnect(); this.mutations?.disconnect(); this.previewResize?.disconnect(); this.previewWatch?.disconnect(); }
  }
  const definitions = { 'ks-header': Header, 'ks-search': Search, 'ks-gallery': Gallery, 'ks-product': Product };
  Object.entries(definitions).forEach(([name, cls]) => { if (!customElements.get(name)) customElements.define(name, cls); });
  document.addEventListener('toggle', e => {
    if (e.target.matches?.('[data-ks-reading]') && e.target.open) track('further_reading_open');
  }, true);
  document.addEventListener('click', e => {
    const link = e.target.closest('a');
    if (!link?.closest('[data-ks]')) return;
    if (link.closest('[data-ks-card]')) track('select_item', { item_id: link.closest('[data-ks-card]').dataset.productHandle });
    else if (link.matches('.ks-button,.ks-link')) track('cta_select', { action: 'navigate' });
  });
  const impressions = new WeakSet();
  const impressionObserver = new IntersectionObserver(entries => entries.forEach(entry => {
    if (entry.isIntersecting && !impressions.has(entry.target)) {
      impressions.add(entry.target); impressionObserver.unobserve(entry.target);
      track('product_impression', { item_id: entry.target.dataset.productHandle });
    }
  }), { threshold: .4 });
  const initialise = (root = document) => {
    root.querySelectorAll('[data-ks-card]').forEach(card => impressionObserver.observe(card));
    root.querySelectorAll('[data-ks-form-success]').forEach(() => track('native_form_success'));
  };
  initialise();
  document.addEventListener('shopify:section:load', e => initialise(e.target));
  document.addEventListener('shopify:section:unload', e => e.target.querySelectorAll('[data-ks-card]').forEach(card => impressionObserver.unobserve(card)));
  window.kehwahSystem = { track, openDialog, bindDialog, closeDialog };
})();
